import { useCallback, useEffect, useMemo, useState } from 'react';
import { getActiveWords, getParagraphIndexByTime } from './utils';
import { TranscriptParagraph } from './types';
import { Word } from '@/player-config/src/types/transcript-types';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getSegmentAtCurrentFrame } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { useRemotionPlayerPause } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPause';
import { getClipFPS } from '@/player-config/src/utils/clipUtils';

export const useTranscriptAutoScroll = ({
  transcriptWithDeletedWordStatus,
  paragraphs,
  scrollToVirtuosoIndex
}: {
  transcriptWithDeletedWordStatus: Record<string, Word[]>;
  paragraphs: TranscriptParagraph[];
  scrollToVirtuosoIndex: (params: { paragraphIndex: number; segmentId: string; wordId?: number }) => void;
}) => {
  const [isAutoScrollDisabled, setIsAutoScrollDisabled] = useState(false);
  const { clipData } = useClipsContext();
  const { isPlaying } = useRemotionPlayerPause();

  const isPaused = useMemo(() => !isPlaying, [isPlaying]);
  const onPreventAutoScroll = useCallback(() => {
    setIsAutoScrollDisabled(true);
  }, []);

  useEffect(() => {
    if (!isPaused) {
      setIsAutoScrollDisabled(false);
    }
  }, [isPaused]);

  const continueAutoScroll = useCallback(() => {
    setIsAutoScrollDisabled(false);
    const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;

    const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
    if (!currentSegment) {
      return;
    }
    const { activeWords } = getActiveWords({ currentTime, transcript: transcriptWithDeletedWordStatus, clipData });
    const startTime =
      activeWords?.length > 0 ? activeWords[0].start_time : currentSegment.fromFrame / getClipFPS(clipData);
    const paragraphIndex = getParagraphIndexByTime(startTime, paragraphs || [], currentSegment.id);
    scrollToVirtuosoIndex({
      paragraphIndex,
      segmentId: currentSegment.id,
      wordId: activeWords.length > 0 ? activeWords[0].index : undefined
    });
  }, [clipData, paragraphs, scrollToVirtuosoIndex, transcriptWithDeletedWordStatus]);

  return { isAutoScrollDisabled, continueAutoScroll, onPreventAutoScroll };
};
